import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import type { PlayerRanking } from "../../../shared/schema";

// Filtre Classement style Padel Now, adapté à PadELO.
// Repris de Padel Now SAUF : "Sans assimilés" et "Progression" (retirés).
// "Français" remplacé par "Padelo uniquement" (joueurs ayant un compte PadELO), décoché par défaut.
// ponytail: Sexe / Ligue / Âge sont dans l'UI mais PadELO n'a pas ces champs côté données
//   → non filtrants pour l'instant (à activer quand le modèle Player les portera).

export type PlayerFilters = {
  sexe: "Hommes" | "Femmes" | null;
  classement: string; // clé de CLASSEMENT
  padeloOnly: boolean;
  ligue: string;
  age: string;
};

// padeloOnly = mode de recherche : true (défaut) = base PadELO ; false = base FFT.
export const DEFAULT_FILTERS: PlayerFilters = {
  sexe: null, classement: "Tous", padeloOnly: true, ligue: "Toutes", age: "Tous",
};

const CLASSEMENT = ["Top 100", "Top 500", "Top 1000", "100-200", "200-500", "500-1000", "1000-10k", "10k+", "Tous"];
const LIGUES = ["Toutes", "Auvergne-Rhône-Alpes", "Bourgogne-Franche-Comté", "Bretagne", "Centre-Val de Loire", "Corse", "Grand Est", "Hauts-de-France", "Île-de-France", "Normandie", "Nouvelle-Aquitaine", "Occitanie", "Pays de la Loire", "PACA"];
const AGES = ["Tous", "-18 ans", "18-25", "26-35 ans", "36-45", "45+"];

// Prédicat : seuls Classement + Padelo filtrent réellement (données dispo). Sexe/Ligue/Âge = no-op.
// Note : padeloOnly n'est pas filtré ici — c'est le mode (PadELO vs FFT), géré par l'écran.
export function applyPlayerFilters(players: PlayerRanking[], f: PlayerFilters): PlayerRanking[] {
  return players.filter((p) => {
    const r = Number(p.rank);
    switch (f.classement) {
      case "Top 100": if (r > 100) return false; break;
      case "Top 500": if (r > 500) return false; break;
      case "Top 1000": if (r > 1000) return false; break;
      case "100-200": if (r <= 100 || r > 200) return false; break;
      case "200-500": if (r <= 200 || r > 500) return false; break;
      case "500-1000": if (r <= 500 || r > 1000) return false; break;
      case "1000-10k": if (r <= 1000 || r > 10000) return false; break;
      case "10k+": if (r <= 10000) return false; break;
      default: break; // "Tous"
    }
    return true;
  });
}

export function activeFilterCount(f: PlayerFilters): number {
  // padeloOnly exclu : c'est le mode de recherche, pas un filtre compté dans le badge.
  let n = 0;
  if (f.sexe) n++;
  if (f.classement !== "Tous") n++;
  if (f.ligue !== "Toutes") n++;
  if (f.age !== "Tous") n++;
  return n;
}

function Pills({ items, value, onPick }: { items: string[]; value: string | null; onPick: (v: string) => void }) {
  return (
    <div className="flex gap-2.5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
      {items.map((it) => (
        <button key={it} onClick={() => onPick(it)}
          className={`shrink-0 rounded-full px-4 py-2.5 font-semibold text-sm transition ${value === it ? "bg-pn-blue text-white" : "bg-pn-blue-soft text-pn-navy"}`}>
          {it}
        </button>
      ))}
    </div>
  );
}

export default function PlayersFilterSheetPN({
  initial, resultCount, onApply, onClose,
}: { initial: PlayerFilters; resultCount: (f: PlayerFilters) => number; onApply: (f: PlayerFilters) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [f, setF] = useState<PlayerFilters>(initial);
  const set = <K extends keyof PlayerFilters>(k: K, v: PlayerFilters[K]) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end justify-center font-body" onClick={onClose}>
      <div className="bg-background w-full max-w-[460px] rounded-t-3xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="w-11 h-1.5 bg-border rounded-full mx-auto mt-2.5" />
        <div className="overflow-y-auto px-5 pb-4">
          <div className="flex items-center justify-between py-3">
            <h2 className="font-head font-extrabold text-2xl text-pn-navy">{t("filters.title", "Filtres")}</h2>
            <button onClick={onClose} className="w-11 h-11 rounded-full bg-muted flex items-center justify-center text-pn-navy"><X className="w-5 h-5" /></button>
          </div>

          <Section title={t("filters.sex", "Sexe")}>
            <div className="flex gap-2.5">
              {(["Hommes", "Femmes"] as const).map((s) => (
                <button key={s} onClick={() => set("sexe", f.sexe === s ? null : s)}
                  className={`rounded-full px-6 py-2.5 font-semibold transition ${f.sexe === s ? "bg-pn-blue text-white" : "bg-pn-blue-soft text-pn-navy"}`}>{s}</button>
              ))}
            </div>
          </Section>

          <Section title={t("filters.ranking", "Classement")}>
            <Pills items={CLASSEMENT} value={f.classement} onPick={(v) => set("classement", v)} />
          </Section>

          {/* Padelo (remplace "Français") */}
          <div className="rounded-2xl border border-border p-4 mt-4 flex items-center gap-3">
            <div className="flex-1">
              <div className="font-head font-bold text-pn-navy text-lg">Padelo</div>
              <div className="text-muted-foreground text-sm">Joueurs uniquement sur Padelo</div>
            </div>
            <button role="switch" aria-checked={f.padeloOnly} onClick={() => set("padeloOnly", !f.padeloOnly)}
              className={`w-14 h-8 rounded-full transition relative ${f.padeloOnly ? "bg-pn-navy" : "bg-muted"}`}>
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${f.padeloOnly ? "left-7" : "left-1"}`} />
            </button>
          </div>

          <Section title={t("filters.league", "Ligue")}>
            <Pills items={LIGUES} value={f.ligue} onPick={(v) => set("ligue", v)} />
          </Section>

          <Section title={t("filters.age", "Âge")}>
            <Pills items={AGES} value={f.age} onPick={(v) => set("age", v)} />
          </Section>
        </div>

        <div className="flex gap-3 p-4 border-t border-border">
          <button onClick={() => setF(DEFAULT_FILTERS)} className="flex items-center justify-center gap-2 bg-pn-blue-soft text-pn-blue font-bold rounded-full px-5 py-3.5">
            <X className="w-4 h-4" /> {t("filters.reset", "Réinitialiser")}
          </button>
          <button onClick={() => onApply(f)} className="flex-1 bg-pn-lime text-pn-navy font-bold rounded-full py-3.5">
            {t("filters.seeResults", "Voir les résultats")} ({resultCount(f)})
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="font-head font-bold text-xl text-pn-navy mb-3">{title}</h3>
      {children}
    </div>
  );
}
